/**
 * Motor de viabilidade — funções puras, aritmética inteira.
 *
 * Não acessa rede, banco nem relógio. Recebe inteiros, devolve inteiros.
 * É o único lugar do sistema onde o dinheiro é calculado, e é por isso que
 * ele tem bateria de teste numérico própria (seção 5.7 do planejamento).
 *
 * Fórmulas: seção 10 do planejamento.
 */

import { BPS, MILLI, aplicarBps, arredondar, emBps, porQuantidade, porUnidade } from './inteiros.js';
import type { CustosAquisicao, CustosVenda, EntradaAnalise, ResultadoAnalise } from './tipos.js';

/** Custo total da aquisição (seção 10.2). */
export function custoTotalAquisicao(a: CustosAquisicao, quantidadeMilli: number): number {
  return (
    porQuantidade(a.precoFornecedorCents, quantidadeMilli) +
    a.freteEntradaCents +
    a.impostosAquisicaoCents +
    a.seguroCents +
    a.inspecaoCents +
    a.armazenagemEntradaCents +
    a.outrosAquisicaoCents
  );
}

/**
 * Quantidade vendável (seção 10.3).
 *
 * A perda entra AQUI e somente aqui. Somá-la também como linha de custo seria
 * contagem dupla e inflaria o custo unitário artificialmente.
 */
export function quantidadeVendavel(quantidadeMilli: number, perdaBps: number): number {
  return aplicarBps(quantidadeMilli, BPS - perdaBps);
}

/** Soma dos percentuais que incidem sobre o preço de venda, em pontos-base. */
function percentuaisSobreVenda(v: CustosVenda): number {
  return (v.comissaoBps ?? 0) + (v.impostoVendaBps ?? 0) + (v.devolucaoBps ?? 0) + (v.custoFinanceiroBps ?? 0);
}

/** Custos de venda em valor fixo por unidade, em centavos. */
function fixosSobreVenda(v: CustosVenda): number {
  return (v.tarifaFixaCents ?? 0) + v.freteSubsidiadoCents + v.custosVendaFixosCents;
}

/** Receita líquida unitária (seção 10.4): PV × (1 − c − t − d − f) − Tf − Fs − Cvf. */
export function receitaLiquidaUnitaria(precoVendaCents: number, v: CustosVenda): number {
  return aplicarBps(precoVendaCents, BPS - percentuaisSobreVenda(v)) - fixosSobreVenda(v);
}

/**
 * Preço de equilíbrio (seção 10.8): (CUE + Tf + Fs + Cvf) ÷ (1 − c − t − d − f).
 *
 * Devolve null quando o denominador é zero ou negativo: os custos percentuais
 * consomem toda a receita e o equilíbrio é matematicamente inexistente.
 * Dividir mesmo assim produziria um preço astronômico e sem sentido.
 *
 * Calculado em duas etapas, em milicentavos, para não arredondar o custo
 * unitário antes da divisão.
 */
export function precoEquilibrio(
  custoTotalCents: number,
  quantidadeVendavelMilli: number,
  v: CustosVenda,
): number | null {
  const denomBps = BPS - percentuaisSobreVenda(v);
  if (denomBps <= 0) return null;
  const numeradorMilicents =
    arredondar(custoTotalCents * MILLI * MILLI, quantidadeVendavelMilli) + fixosSobreVenda(v) * MILLI;
  return arredondar(numeradorMilicents * BPS, denomBps * MILLI);
}

/** Preço necessário para atingir uma margem-alvo (seção 10.8). Null pela mesma razão acima. */
export function precoAlvo(
  custoTotalCents: number,
  quantidadeVendavelMilli: number,
  v: CustosVenda,
  margemAlvoBps: number,
): number | null {
  const denomBps = BPS - percentuaisSobreVenda(v) - margemAlvoBps;
  if (denomBps <= 0) return null;
  const numeradorMilicents =
    arredondar(custoTotalCents * MILLI * MILLI, quantidadeVendavelMilli) + fixosSobreVenda(v) * MILLI;
  return arredondar(numeradorMilicents * BPS, denomBps * MILLI);
}

/** Parâmetros de venda que o sistema nunca preenche sozinho (RN-051). */
const OBRIGATORIOS: ReadonlyArray<readonly [keyof CustosVenda, string]> = [
  ['comissaoBps', 'Comissão do marketplace'],
  ['tarifaFixaCents', 'Tarifa fixa por venda'],
  ['impostoVendaBps', 'Impostos sobre a venda'],
  ['devolucaoBps', 'Provisão de devoluções'],
  ['custoFinanceiroBps', 'Custo financeiro'],
];

export function parametrosFaltando(v: CustosVenda): string[] {
  return OBRIGATORIOS.filter(([campo]) => v[campo] === null || v[campo] === undefined).map(([, rotulo]) => rotulo);
}

/**
 * Análise completa.
 *
 * Duas decisões de modelagem que mudam o resultado e por isso ficam explícitas:
 *
 * 1. O lucro total sai dos TOTAIS, não do lucro unitário arredondado. Expressar
 *    um total exato como valor por unidade perde centavos, e multiplicá-los de
 *    volta propaga o erro. O lucro unitário é uma visão arredondada do total.
 *
 * 2. Quando não se vende tudo, o custo é alocado na proporção do que foi
 *    vendido, e o estoque que sobrou aparece em `capitalNaoRecuperadoCents`.
 *    Debitar o custo total contra uma venda parcial trataria o encalhe como
 *    perda definitiva; ignorá-lo trataria como se fosse de graça. Nenhum dos
 *    dois é verdade: é capital parado, e o cenário pessimista mostra isso
 *    numa linha própria em vez de esconder no lucro.
 */
export function analisar(e: EntradaAnalise): ResultadoAnalise {
  if (e.quantidadeMilli <= 0) throw new Error('quantidade deve ser maior que zero');

  const custoTotalAquisicaoCents = custoTotalAquisicao(e.aquisicao, e.quantidadeMilli);
  const quantidadeVendavelMilli = quantidadeVendavel(e.quantidadeMilli, e.aquisicao.perdaBps);
  if (quantidadeVendavelMilli <= 0) throw new Error('perda de 100% não deixa quantidade vendável');
  const custoUnitarioEfetivoCents = porUnidade(custoTotalAquisicaoCents, quantidadeVendavelMilli);
  const base = { custoTotalAquisicaoCents, quantidadeVendavelMilli, custoUnitarioEfetivoCents };

  const faltando = parametrosFaltando(e.venda);
  if (faltando.length > 0) return { completo: false, faltando, ...base };

  const receitaLiquidaUnitCents = receitaLiquidaUnitaria(e.precoVendaCents, e.venda);
  const quantidadeVendidaMilli = aplicarBps(quantidadeVendavelMilli, e.pctVendidoBps);
  const receitaLiquidaTotalCents = porQuantidade(receitaLiquidaUnitCents, quantidadeVendidaMilli);

  // O custo de aquisição é alocado na proporção do que foi vendido. Vendendo
  // tudo, a alocação é o custo total exato — sem passar pelo custo unitário
  // arredondado, que perderia centavos ao ser multiplicado de volta.
  const custoAlocadoCents = arredondar(custoTotalAquisicaoCents * quantidadeVendidaMilli, quantidadeVendavelMilli);
  const capitalNaoRecuperadoCents = custoTotalAquisicaoCents - custoAlocadoCents;
  const lucroTotalCents = receitaLiquidaTotalCents - custoAlocadoCents;
  const lucroUnitarioCents = receitaLiquidaUnitCents - custoUnitarioEfetivoCents;

  const antecipadosCents = porQuantidade(e.venda.custosVendaAntecipadosCents, quantidadeVendavelMilli);
  const semReservaCents = custoTotalAquisicaoCents + antecipadosCents;
  const capitalNecessarioCents = semReservaCents + aplicarBps(semReservaCents, e.reservaSegurancaBps);

  // Markup a partir dos totais, sem passar pelo custo unitário arredondado.
  const receitaBrutaCents = porQuantidade(e.precoVendaCents, quantidadeVendavelMilli);
  const precoEquilibrioCents = precoEquilibrio(custoTotalAquisicaoCents, quantidadeVendavelMilli, e.venda);

  return {
    completo: true,
    faltando: [],
    ...base,
    quantidadeVendidaMilli,
    receitaLiquidaUnitCents,
    custoAlocadoCents,
    capitalNaoRecuperadoCents,
    lucroUnitarioCents,
    lucroTotalCents,
    capitalNecessarioCents,
    margemBps: e.precoVendaCents === 0 ? 0 : emBps(lucroUnitarioCents, e.precoVendaCents),
    roiBps: capitalNecessarioCents === 0 ? 0 : emBps(lucroTotalCents, capitalNecessarioCents),
    markupBps:
      custoTotalAquisicaoCents === 0
        ? 0
        : emBps(receitaBrutaCents - custoTotalAquisicaoCents, custoTotalAquisicaoCents),
    precoEquilibrioCents,
    margemSegurancaBps:
      precoEquilibrioCents === null || e.precoVendaCents === 0
        ? null
        : emBps(e.precoVendaCents - precoEquilibrioCents, e.precoVendaCents),
  };
}
